/**
 * ImageViewerModal — fullscreen popup for viewing and editing generated images.
 * Inspired by ChatGPT's image canvas UI.
 *
 * Modes:
 *   'view'  — default, shows image + text edit input at bottom
 *   'brush' — brush/inpainting mode, shows canvas overlay + mask prompt input
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'motion/react'
import {
  X,
  Download,
  Share2,
  Paintbrush,
  ChevronDown,
  Undo2,
  Redo2,
  Mic,
  ArrowUp,
  Plus,
  Loader2,
} from 'lucide-react'

// ── Aspect ratio options (matching ChatGPT dropdown) ─────────────────────────
export const ASPECT_RATIOS = [
  { label: 'Kvadrat', value: '1:1' },
  { label: 'Landskap', value: '5:4' },
  { label: 'Story', value: '9:16' },
  { label: 'Ultrabred', value: '21:9' },
  { label: 'Bredskjerm', value: '16:9' },
  { label: 'Landskap', value: '4:3' },
  { label: 'Bred', value: '3:2' },
  { label: 'Standard', value: '4:5' },
  { label: 'Portrett', value: '3:4' },
  { label: 'Høy', value: '2:3' },
] as const

export type AspectRatioValue = (typeof ASPECT_RATIOS)[number]['value']

// ── Props ─────────────────────────────────────────────────────────────────────
export interface ImageViewerModalProps {
  imageUrl: string
  prompt?: string
  language?: 'no' | 'en'
  onClose: () => void
  /** Called when user submits a text edit prompt */
  onEditSubmit?: (prompt: string, imageUrl: string) => void
  /** Called when user submits a brush/inpaint prompt with mask */
  onInpaintSubmit?: (prompt: string, imageUrl: string, maskDataUrl: string) => void
  /** Called when user selects an aspect ratio — triggers a new generation */
  onAspectRatioChange?: (ratio: AspectRatioValue, imageUrl: string) => void
  /** Whether an edit is currently processing */
  isEditing?: boolean
  /** New image URL after edit completes */
  editedImageUrl?: string | null
}

// ── Main component ────────────────────────────────────────────────────────────
export function ImageViewerModal({
  imageUrl,
  prompt,
  language = 'no',
  onClose,
  onEditSubmit,
  onInpaintSubmit,
  onAspectRatioChange,
  isEditing = false,
  editedImageUrl,
}: ImageViewerModalProps) {
  const no = language !== 'en'

  // ── State ──────────────────────────────────────────────────────────────────
  const [mode, setMode] = useState<'view' | 'brush'>('view')
  const [editInput, setEditInput] = useState('')
  const [showAspectDropdown, setShowAspectDropdown] = useState(false)
  const [currentAspect, setCurrentAspect] = useState<AspectRatioValue>('16:9')

  // History for undo/redo (stores image URLs)
  const [history, setHistory] = useState<string[]>([imageUrl])
  const [historyIndex, setHistoryIndex] = useState(0)
  const currentUrl = editedImageUrl ?? history[historyIndex]

  // Brush state
  const [isDrawing, setIsDrawing] = useState(false)
  const [brushSize, setBrushSize] = useState(48)
  const [hasMask, setHasMask] = useState(false)
  const brushInputRef = useRef<HTMLInputElement>(null)

  // Canvas refs
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null)
  const maskCanvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Marching ants animation
  const marchingAntsRef = useRef<number>(0)
  const dashOffsetRef = useRef<number>(0)

  // ── Sync history when editedImageUrl changes ───────────────────────────────
  useEffect(() => {
    if (editedImageUrl && editedImageUrl !== history[historyIndex]) {
      const newHistory = history.slice(0, historyIndex + 1)
      newHistory.push(editedImageUrl)
      setHistory(newHistory)
      setHistoryIndex(newHistory.length - 1)
    }
  }, [editedImageUrl]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Close on Escape ────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (mode === 'brush') setMode('view')
        else onClose()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [mode, onClose])

  // ── Lock body scroll ───────────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  // ── Canvas setup when entering brush mode ─────────────────────────────────
  useEffect(() => {
    if (mode !== 'brush') {
      cancelAnimationFrame(marchingAntsRef.current)
      return
    }
    const img = imageRef.current
    const overlay = overlayCanvasRef.current
    const mask = maskCanvasRef.current
    if (!img || !overlay || !mask) return

    const resize = () => {
      const w = img.offsetWidth
      const h = img.offsetHeight
      overlay.width = w
      overlay.height = h
      mask.width = w
      mask.height = h
      // Clear
      const ctx = overlay.getContext('2d')!
      ctx.clearRect(0, 0, w, h)
      const mctx = mask.getContext('2d')!
      mctx.fillStyle = 'black'
      mctx.fillRect(0, 0, w, h)
    }
    resize()
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [mode])

  // ── Marching ants animation ────────────────────────────────────────────────
  const drawMarchingAnts = useCallback(() => {
    const overlay = overlayCanvasRef.current
    const mask = maskCanvasRef.current
    if (!overlay || !mask) return
    const ctx = overlay.getContext('2d')!
    const mctx = mask.getContext('2d')!
    const { width: w, height: h } = overlay

    // Redraw blue overlay from mask
    ctx.clearRect(0, 0, w, h)
    const maskData = mctx.getImageData(0, 0, w, h)
    const overlayData = ctx.createImageData(w, h)
    for (let i = 0; i < maskData.data.length; i += 4) {
      if (maskData.data[i] > 128) {
        // White pixel in mask → blue overlay
        overlayData.data[i] = 59
        overlayData.data[i + 1] = 130
        overlayData.data[i + 2] = 246
        overlayData.data[i + 3] = 128 // 50% opacity
      }
    }
    ctx.putImageData(overlayData, 0, 0)

    // Draw marching ants border
    if (hasMask) {
      dashOffsetRef.current = (dashOffsetRef.current + 0.5) % 16
      ctx.save()
      ctx.strokeStyle = 'white'
      ctx.lineWidth = 1.5
      ctx.setLineDash([8, 8])
      ctx.lineDashOffset = -dashOffsetRef.current
      // Simple bounding box outline of mask for now
      // (full contour tracing is complex; bounding box is a good approximation)
      let minX = w, minY = h, maxX = 0, maxY = 0
      const md = mctx.getImageData(0, 0, w, h)
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          if (md.data[(y * w + x) * 4] > 128) {
            if (x < minX) minX = x
            if (x > maxX) maxX = x
            if (y < minY) minY = y
            if (y > maxY) maxY = y
          }
        }
      }
      if (maxX > minX && maxY > minY) {
        ctx.strokeRect(minX - 2, minY - 2, maxX - minX + 4, maxY - minY + 4)
      }
      ctx.restore()
    }

    marchingAntsRef.current = requestAnimationFrame(drawMarchingAnts)
  }, [hasMask])

  useEffect(() => {
    if (mode === 'brush') {
      marchingAntsRef.current = requestAnimationFrame(drawMarchingAnts)
    }
    return () => cancelAnimationFrame(marchingAntsRef.current)
  }, [mode, drawMarchingAnts])

  // ── Brush drawing helpers ──────────────────────────────────────────────────
  const getCanvasPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = overlayCanvasRef.current!
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    let clientX: number, clientY: number
    if ('touches' in e) {
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
    } else {
      clientX = (e as React.MouseEvent).clientX
      clientY = (e as React.MouseEvent).clientY
    }
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    }
  }

  const paintAt = (x: number, y: number) => {
    const mask = maskCanvasRef.current!
    const mctx = mask.getContext('2d')!
    mctx.fillStyle = 'white'
    mctx.beginPath()
    mctx.arc(x, y, brushSize / 2, 0, Math.PI * 2)
    mctx.fill()
    setHasMask(true)
  }

  const handleBrushMouseDown = (e: React.MouseEvent) => {
    setIsDrawing(true)
    const pos = getCanvasPos(e)
    paintAt(pos.x, pos.y)
  }

  const handleBrushMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing) return
    const pos = getCanvasPos(e)
    paintAt(pos.x, pos.y)
  }

  const handleBrushMouseUp = () => setIsDrawing(false)

  const clearMask = () => {
    const mask = maskCanvasRef.current
    if (!mask) return
    const mctx = mask.getContext('2d')!
    mctx.fillStyle = 'black'
    mctx.fillRect(0, 0, mask.width, mask.height)
    const overlay = overlayCanvasRef.current
    if (overlay) overlay.getContext('2d')!.clearRect(0, 0, overlay.width, overlay.height)
    setHasMask(false)
  }

  const exportMask = (): string => {
    const mask = maskCanvasRef.current!
    const mctx = mask.getContext('2d')!
    const { width: w, height: h } = mask
    // Convert: white → transparent (area to edit), black → opaque black (keep)
    const data = mctx.getImageData(0, 0, w, h)
    const out = new ImageData(w, h)
    for (let i = 0; i < data.data.length; i += 4) {
      const isWhite = data.data[i] > 128
      out.data[i] = 0
      out.data[i + 1] = 0
      out.data[i + 2] = 0
      out.data[i + 3] = isWhite ? 0 : 255 // transparent = edit area
    }
    const tmpCanvas = document.createElement('canvas')
    tmpCanvas.width = w
    tmpCanvas.height = h
    tmpCanvas.getContext('2d')!.putImageData(out, 0, 0)
    return tmpCanvas.toDataURL('image/png')
  }

  // ── Undo / Redo ────────────────────────────────────────────────────────────
  const canUndo = historyIndex > 0
  const canRedo = historyIndex < history.length - 1

  const handleUndo = () => {
    if (canUndo) setHistoryIndex(i => i - 1)
  }
  const handleRedo = () => {
    if (canRedo) setHistoryIndex(i => i + 1)
  }

  // ── Download ───────────────────────────────────────────────────────────────
  const handleDownload = async () => {
    try {
      const response = await fetch(currentUrl)
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sine-${Date.now()}.png`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      // Fallback for cross-origin images
      const a = document.createElement('a')
      a.href = currentUrl
      a.download = `sine-${Date.now()}.png`
      a.target = '_blank'
      a.click()
    }
  }

  // ── Share ──────────────────────────────────────────────────────────────────
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ url: currentUrl, title: prompt || 'Generert bilde' })
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(currentUrl)
    }
  }

  // ── Submit handlers ────────────────────────────────────────────────────────
  const handleEditSubmit = () => {
    if (!editInput.trim() || isEditing) return
    onEditSubmit?.(editInput.trim(), currentUrl)
    setEditInput('')
    onClose() // Auto-close after submitting edit
  }

  const handleBrushSubmit = () => {
    if (!editInput.trim() || !hasMask || isEditing) return
    const maskDataUrl = exportMask()
    onInpaintSubmit?.(editInput.trim(), currentUrl, maskDataUrl)
    setEditInput('')
    clearMask()
    setMode('view')
    onClose() // Auto-close after submitting inpaint
  }

  const handleAspectSelect = (ratio: AspectRatioValue) => {
    setCurrentAspect(ratio)
    setShowAspectDropdown(false)
    onAspectRatioChange?.(ratio, currentUrl)
    onClose()
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  const currentRatioLabel = ASPECT_RATIOS.find(r => r.value === currentAspect)?.label ?? 'Høyde/bredde-forhold'

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          background: '#111111',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      >
        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
          minHeight: 56,
        }}>
          {mode === 'brush' ? (
            <>
              {/* Brush mode header */}
              <button
                onClick={() => { setMode('view'); clearMask() }}
                style={headerBtnStyle}
                title={no ? 'Avbryt' : 'Cancel'}
              >
                <X size={16} />
              </button>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#E5E5E5', flex: 1 }}>
                {no ? 'Rediger utvalg' : 'Edit selection'}
              </span>
              <button onClick={handleUndo} disabled={!canUndo} style={headerBtnStyle} title="Angre">
                <Undo2 size={15} />
              </button>
              <button onClick={handleRedo} disabled={!canRedo} style={headerBtnStyle} title="Gjør om">
                <Redo2 size={15} />
              </button>
              <button
                onClick={() => { setMode('view'); clearMask() }}
                style={{ ...headerBtnStyle, color: '#999' }}
              >
                {no ? 'Avbryt' : 'Cancel'}
              </button>
            </>
          ) : (
            <>
              {/* View mode header */}
              <button onClick={onClose} style={headerBtnStyle} title={no ? 'Lukk' : 'Close'}>
                <X size={16} />
              </button>
              <span style={{
                fontSize: 14,
                fontWeight: 500,
                color: '#E5E5E5',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                marginRight: 8,
              }}>
                {prompt || (no ? 'Generert bilde' : 'Generated image')}
              </span>

              {/* Velg / Brush button */}
              <button
                onClick={() => setMode('brush')}
                style={{
                  ...headerBtnStyle,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 20,
                  fontSize: 13,
                }}
                title={no ? 'Velg område å redigere' : 'Select area to edit'}
              >
                <Paintbrush size={14} />
                {no ? 'Velg' : 'Select'}
              </button>

              {/* Aspect ratio dropdown */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowAspectDropdown(v => !v)}
                  style={{
                    ...headerBtnStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 20,
                    fontSize: 13,
                  }}
                >
                  <span style={{ fontSize: 12 }}>⊞</span>
                  {currentRatioLabel}
                  <ChevronDown size={13} />
                </button>
                <AnimatePresence>
                  {showAspectDropdown && (
                    <AspectRatioDropdown
                      language={language}
                      currentValue={currentAspect}
                      onSelect={handleAspectSelect}
                      onClose={() => setShowAspectDropdown(false)}
                    />
                  )}
                </AnimatePresence>
              </div>

              {/* Share */}
              <button onClick={handleShare} style={headerBtnStyle} title={no ? 'Del' : 'Share'}>
                <Share2 size={15} />
              </button>

              {/* Download */}
              <button onClick={handleDownload} style={headerBtnStyle} title={no ? 'Last ned' : 'Download'}>
                <Download size={15} />
              </button>

              {/* Undo/Redo */}
              {history.length > 1 && (
                <>
                  <button onClick={handleUndo} disabled={!canUndo} style={headerBtnStyle} title="Angre">
                    <Undo2 size={15} />
                  </button>
                  <button onClick={handleRedo} disabled={!canRedo} style={headerBtnStyle} title="Gjør om">
                    <Redo2 size={15} />
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {/* ── IMAGE SECTION ──────────────────────────────────────────────── */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px 32px',
          overflow: 'hidden',
          position: 'relative',
        }}>
          <div
            ref={containerRef}
            style={{
              position: 'relative',
              maxWidth: '100%',
              maxHeight: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Loading overlay when editing */}
            {isEditing && (
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0,0,0,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: 8,
              }}>
                <Loader2 size={32} color="#1A93FE" style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            )}

            <img
              ref={imageRef}
              src={currentUrl}
              alt={prompt || 'Generert bilde'}
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 200px)',
                objectFit: 'contain',
                borderRadius: 8,
                display: 'block',
                userSelect: 'none',
              }}
            />

            {/* Brush canvas overlay */}
            {mode === 'brush' && (
              <>
                <canvas
                  ref={maskCanvasRef}
                  style={{ display: 'none' }}
                />
                <canvas
                  ref={overlayCanvasRef}
                  onMouseDown={handleBrushMouseDown}
                  onMouseMove={handleBrushMouseMove}
                  onMouseUp={handleBrushMouseUp}
                  onMouseLeave={handleBrushMouseUp}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    cursor: `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='${brushSize}' height='${brushSize}'><circle cx='${brushSize / 2}' cy='${brushSize / 2}' r='${brushSize / 2 - 1}' fill='rgba(59,130,246,0.3)' stroke='white' stroke-width='1.5'/></svg>") ${brushSize / 2} ${brushSize / 2}, crosshair`,
                    borderRadius: 8,
                    touchAction: 'none',
                  }}
                />
              </>
            )}
          </div>
        </div>

        {/* ── BOTTOM INPUT ───────────────────────────────────────────────── */}
        <div style={{
          padding: '16px 24px 24px',
          flexShrink: 0,
        }}>
          {/* Brush size slider (brush mode only) */}
          {mode === 'brush' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 12,
              justifyContent: 'center',
            }}>
              <span style={{ fontSize: 12, color: '#666' }}>
                {no ? 'Penselstørrelse' : 'Brush size'}
              </span>
              <input
                type="range"
                min={10}
                max={120}
                value={brushSize}
                onChange={e => setBrushSize(Number(e.target.value))}
                style={{ width: 120, accentColor: '#1A93FE' }}
              />
              <span style={{ fontSize: 12, color: '#666', width: 28 }}>{brushSize}px</span>
              {hasMask && (
                <button
                  onClick={clearMask}
                  style={{ fontSize: 12, color: '#999', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {no ? 'Tøm' : 'Clear'}
                </button>
              )}
            </div>
          )}

          {/* Chat input */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: '#2A2A2A',
            borderRadius: 28,
            padding: '8px 8px 8px 16px',
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <button style={{ color: '#666', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
              <Plus size={16} />
            </button>
            <input
              ref={brushInputRef}
              type="text"
              value={editInput}
              onChange={e => setEditInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  if (mode === 'brush') handleBrushSubmit()
                  else handleEditSubmit()
                }
              }}
              placeholder={
                mode === 'brush'
                  ? (no ? 'Beskriv hva som skal endres i utvalget...' : 'Describe what to change in the selection...')
                  : (no ? 'Beskriv redigeringer' : 'Describe edits')
              }
              style={{
                flex: 1,
                background: 'none',
                border: 'none',
                outline: 'none',
                color: '#E5E5E5',
                fontSize: 14,
              }}
            />
            <button style={{ color: '#666', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
              <Mic size={16} />
            </button>
            <button
              onClick={mode === 'brush' ? handleBrushSubmit : handleEditSubmit}
              disabled={!editInput.trim() || isEditing || (mode === 'brush' && !hasMask)}
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: editInput.trim() && !isEditing && (mode !== 'brush' || hasMask)
                  ? '#E5E5E5'
                  : '#3A3A3A',
                border: 'none',
                cursor: editInput.trim() && !isEditing ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'background 0.15s',
              }}
            >
              {isEditing
                ? <Loader2 size={14} color="#666" style={{ animation: 'spin 1s linear infinite' }} />
                : <ArrowUp size={14} color={editInput.trim() ? '#111' : '#666'} />
              }
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  )
}

// ── Aspect Ratio Dropdown ─────────────────────────────────────────────────────
function AspectRatioDropdown({
  language,
  currentValue,
  onSelect,
  onClose,
}: {
  language: 'no' | 'en'
  currentValue: AspectRatioValue
  onSelect: (v: AspectRatioValue) => void
  onClose: () => void
}) {
  const no = language !== 'en'

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-aspect-dropdown]')) onClose()
    }
    setTimeout(() => document.addEventListener('mousedown', handler), 0)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  return (
    <motion.div
      data-aspect-dropdown
      initial={{ opacity: 0, y: -8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.96 }}
      transition={{ duration: 0.12 }}
      style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        right: 0,
        background: '#2A2A2A',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 12,
        padding: '8px 0',
        minWidth: 200,
        zIndex: 10000,
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}
    >
      <div style={{ padding: '6px 16px 10px', fontSize: 12, color: '#888' }}>
        {no
          ? 'Generer dette bildet med et annet høyde/bredde-forhold'
          : 'Generate this image with a different aspect ratio'}
      </div>
      {ASPECT_RATIOS.map(r => (
        <button
          key={r.value}
          onClick={() => onSelect(r.value)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: '8px 16px',
            background: r.value === currentValue ? 'rgba(255,255,255,0.06)' : 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#E5E5E5',
            fontSize: 14,
            textAlign: 'left',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
          onMouseLeave={e => (e.currentTarget.style.background = r.value === currentValue ? 'rgba(255,255,255,0.06)' : 'none')}
        >
          <span>{r.label}</span>
          <span style={{ color: '#666', fontSize: 13 }}>{r.value}</span>
        </button>
      ))}
    </motion.div>
  )
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const headerBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#CCCCCC',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 6,
  borderRadius: 6,
  flexShrink: 0,
  transition: 'color 0.1s, background 0.1s',
}
